import React, {ReactElement} from "react"
import {useQueryState} from "use-location-state"
import {startCase} from "lodash"
import TitleBar from "../../components/TitleBar/TitleBar"
import {TabsMeta, TitleBarTabs} from "../../components/TitleBarTabs/TitleBarTabs"
import {MainSectionTable} from "../../containers/MainSection/MainSection"
import {useAccessToken, useIsBankUser, useIsUnitUser, useUserClaimsData} from "../../services/auth"
import {AssigneeType, AssignmentResourceType, AssignmentStage} from "../../resources/assignments"
import {AssignmentsAmountIndicator} from "../../components/AssignmentAmountIndicator/AssignmentsAmountIndicator"
import ReceivedPayments, {ReceivedPaymentsColumns} from "../../components/ReceivedPayments/ReceivedPayments"
import {AssignmentsHub, AssignmentsHubColumns} from "../AssignmentsHub/AssignmentsHub"

enum ReceivedPaymentsTabs {
	All = "All",
	Assignments = "Assignments",
}

export default function ReceivedPaymentsPage(): ReactElement {
	const isUnitUser = useIsUnitUser()
	const isBankUser = useIsBankUser()
	const accessToken = useAccessToken()
	const claims = useUserClaimsData()
	const liClassName = "assignments-tab-li" as const
	const commonTabProps = {liClassName}
	const assigneeType = startCase(claims.userType) as AssigneeType

	const [currentTab, setCurrentTab] = useQueryState<ReceivedPaymentsTabs>("tab", ReceivedPaymentsTabs.All)

	const tabs: TabsMeta<ReceivedPaymentsTabs>[] = [
		{label: "All", key: ReceivedPaymentsTabs.All},
		{
			label: "Assignments",
			key: ReceivedPaymentsTabs.Assignments,
			suffixElement: (
				<AssignmentsAmountIndicator
					findAssignmentsArgs={{
						accessToken,
						assignees: [assigneeType],
						stages: [AssignmentStage.Pending],
						limit: 0,
						resourceTypes: [AssignmentResourceType.WireReceivedPayment],
					}}
					isActive={currentTab == ReceivedPaymentsTabs.Assignments}
					tooltip={
						<>
							Pending assignment
							<br /> for {assigneeType} user
						</>
					}
				/>
			),
		},
	].map((tab) => ({...tab, ...commonTabProps}))

	const assignmentHubIncludedColumns = [
		AssignmentsHubColumns.isOwner,
		AssignmentsHubColumns.resourceType,
		AssignmentsHubColumns.resourceId,
		...(isUnitUser || isBankUser ? [AssignmentsHubColumns.org] : []),
		...(isUnitUser ? [AssignmentsHubColumns.bank] : []),
		AssignmentsHubColumns.customer,
		AssignmentsHubColumns.assignmentId,
		AssignmentsHubColumns.assignmentType,
		AssignmentsHubColumns.assignee,
		AssignmentsHubColumns.assignmentStatus,
		AssignmentsHubColumns.createdAt,
	]

	return (
		<>
			<TitleBar title={"Received Payments"} noBorder />
			<TitleBarTabs
				currentTab={currentTab}
				setCurrentTab={setCurrentTab}
				tabs={tabs}
				navClassName="assignments-tab-nav"
			/>

			<MainSectionTable>
				{currentTab == ReceivedPaymentsTabs.All && (
					<ReceivedPayments
						fullHeight={true}
						showTabsOnHeader
						enableOrgFilter={true}
						enableStatusFilter={true}
						enableTypeFilter={true}
						enableAmountFilter={true}
						disableDrawer={useIsUnitUser()}
						includedColumns={[
							ReceivedPaymentsColumns.id,
							ReceivedPaymentsColumns.type,
							...(useIsUnitUser() || useIsBankUser() ? [ReceivedPaymentsColumns.org] : []),
							ReceivedPaymentsColumns.accountId,
							ReceivedPaymentsColumns.customer,
							ReceivedPaymentsColumns.companyName,
							ReceivedPaymentsColumns.amount,
							ReceivedPaymentsColumns.status,
							ReceivedPaymentsColumns.createdAt,
							ReceivedPaymentsColumns.completionDate,
						]}
					/>
				)}
				{currentTab == ReceivedPaymentsTabs.Assignments && (
					<AssignmentsHub
						token={accessToken}
						includedColumns={assignmentHubIncludedColumns}
						enableResourceTypeFilter={false}
						resourceTypes={[AssignmentResourceType.WireReceivedPayment]}
						enableAssignmentTypeFilter={false}
						enableOrgFilter={isUnitUser || isBankUser}
						enableBankFilter={isUnitUser}
						fullHeight
						enableAssigneeFilter
						enableAssignmentStageFilter
						enableAssignmentIsEscalatedFilter
					/>
				)}
			</MainSectionTable>
		</>
	)
}
